import { memo } from 'react';
import { useDispatch } from 'react-redux';
import { cameraStreamManager } from 'services/CameraStream/CameraStreamManager';
import { transactionActions } from 'store/transactionsSlice/transactionsSlice';
import useKeyNavigation from 'hooks/transaction/useKeyNavigation';
import useTakeSnapshot from '../../MediaSection/hooks/useTakeSnapshot';
import useFaceMatching from '../../MediaSection/hooks/useFaceMatching';

import CameraStreamFooterName from './CameraStreamFooterName';
import CameraStreamFooterActions from './CameraStreamFooterActions/CameraStreamFooterActions';
import { Tooltip } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

import styles from './CameraStreamFooter.module.css';
import PTZRequstStatus from './PTZRequestStatus/PTZRequestStatus';

function CameraStreamFooter({
    hovered,
    isVideoLoading,
    videoFailedToLoad,
    camConfig,
    refreshVideoStreamHandler,
    hardRefreshVideoStreamHandler,
    level1,
    openDialogHandler,
    tabAudioConfig,
    tabId,
    expandedCamId,
    onToggleAudio,
    enableDemoAudioFile,
    isFullScreenRequested,
    footerMode,
    cameraIssues,
    transactionId,
    camInfo,
    videoWallCameras,
    rotation,
    togglePTZ,
    isPTZOn,
    isCameraFocused,
    isPTZCamera,
    ptzRequestStatus,
    fullZoomIn,
    fullZoomOut,
    isDisabledSnapshot,
    streamingCameraIds = [],
    previousStream,
    nextStream,
    isCameraFreezed,
    isStreamTestPage = false,
    visibleCameraIds,
    openWindowVideoIdOutput,
    isTabActive,
}) {
    const dispatch = useDispatch();
    const hasCameraIssues = cameraIssues?.length > 0;

    const isMtxDirect = camConfig.mtxDirect;
    const { camId } = camConfig;

    const { isTabMuted, soundOnStreamId, availableAudioStreams } = tabAudioConfig || {};

    const camVideoId = cameraStreamManager.getVideoElementId(tabId, camConfig.id);
    const expandedCamVideoId = typeof expandedCamId === 'string' && expandedCamId ? cameraStreamManager.getVideoElementId(tabId, expandedCamId) : '';
    const showArrows =
        !footerMode && !isFullScreenRequested && !videoWallCameras && expandedCamVideoId === camVideoId && streamingCameraIds?.length > 1;
    const audioEnabled = !isTabMuted && soundOnStreamId === camVideoId;
    const isAudioAvailable = availableAudioStreams?.includes(camVideoId);

    const footerContainerStyles = `${styles['tc-cs-footer']}${hovered ? ` ${styles['tc-cs-footer--hovered']}` : ''}${isMtxDirect ? ` ${styles['tc-cs-footer--mtx']}` : ''} ${!isVideoLoading && !videoFailedToLoad ? ` ${styles['tc-cs-footer--box-shadow']}` : ''}${
        (expandedCamId && expandedCamId !== camConfig.id && !level1) || tabId === 'wall' ? ` ${styles['tc-cs-footer--L2-shrinked']}` : ''
    } ${(hovered || !videoWallCameras) && styles['hasOpacity']}${expandedCamId && expandedCamId === camConfig.id ? ` ${styles['tc-cs-footer--expanded']}` : ''}`;
    const footerItemsStyle = `${styles['tc-cs-footer-action-items']} ${expandedCamId === camConfig.id ? `${styles['tc-cs-footer-action-items--expanded']}` : ''}`;
    const footerNameStyle = `${styles['tc-cs-footer-name']} ${isStreamTestPage ? styles['tc-cs-footer-name-stream-test'] : ''}`;

    const title = camConfig.name ?? '';
    const gateName = camConfig.gateName ?? '';
    const direction = camConfig.direction ?? '';
    const level = camConfig?.level ?? '';
    const isCameraActive = camConfig?.status !== 'INACTIVE';
    const isTransactionPage = typeof transactionId === 'number';
    const currentCamera = (streamingCameraIds?.findIndex((cam) => cam === String(camConfig.id)) ?? -1) + 1;

    const clickHandlerCameraBugIcon = (state) => {
        dispatch(transactionActions.setReportIssuesModalState(state));
        dispatch(transactionActions.setSelectedCameraIssues(cameraIssues));
        if (transactionId === 'videoWall') {
            dispatch(transactionActions.setSelectedIssueUrl('/transactions/trucks/detection/issue'));
        } else {
            dispatch(transactionActions.setSelectedIssueUrl(`/transactions/trucks/${transactionId}/issue`));
        }
        dispatch(transactionActions.setIssueTitle(cameraIssues[0].camera.name));
        dispatch(transactionActions.setIssueDataLoggs(camInfo));
    };

    const stopPropagationHandler = (e) => e.stopPropagation();

    const isCameraZoomed = isTabActive && Boolean(expandedCamId) && expandedCamId === camConfig.id;

    const { onTakeSnapshot, snapshots } = useTakeSnapshot({
        transactionId,
        streamingCameraIds,
        tabId,
        camInfo,
        rotation,
        title,
        isDisabledSnapshot,
        expandedCamVideoId: isCameraZoomed ? expandedCamVideoId : '',
    });

    const { handleFaceMatch } = useFaceMatching({
        camInfo,
        tabId,
        transactionId,
    });

    const handleZoomedSnapshotShortcut = () => {
        if (isCameraZoomed) onTakeSnapshot();
    };

    useKeyNavigation([{ altKey: true, shiftKey: false, code: 'KeyS', handleClick: handleZoomedSnapshotShortcut }], isTransactionPage);

    const onPreviousStream = () => {
        if (previousStream) previousStream();
    };

    const onNextStream = () => {
        if (nextStream) nextStream();
    };

    return (
        <>
            <div className={footerContainerStyles} onClick={stopPropagationHandler} onDoubleClick={stopPropagationHandler}>
                <div className={styles['tc-cs-footer--left']}>
                    {showArrows && (
                        <div className={footerItemsStyle}>
                            <Tooltip title="Alt + ←" placement="top-start">
                                <div className={`${styles['arrow']}`} onDoubleClick={stopPropagationHandler} onClick={onPreviousStream}>
                                    <ArrowBackIosNewIcon fontSize="small" style={{ cursor: 'pointer', width: '24px', height: '24px' }} />
                                </div>
                            </Tooltip>
                        </div>
                    )}
                    {showArrows && <span>{`${currentCamera}/${streamingCameraIds?.length}`}</span>}
                    {showArrows && (
                        <div className={footerItemsStyle}>
                            <Tooltip title="Alt + →" placement="top-start">
                                <div className={`${styles['arrow']}`} onDoubleClick={stopPropagationHandler} onClick={onNextStream}>
                                    <ArrowForwardIosIcon fontSize="small" style={{ cursor: 'pointer', width: '24px', height: '24px' }} />
                                </div>
                            </Tooltip>
                        </div>
                    )}
                    <Tooltip title={title} placement="top-start">
                        <p className={footerNameStyle}>{title}</p>
                    </Tooltip>
                    {footerMode && gateName && (
                        <CameraStreamFooterName
                            footerDebugMode={footerMode}
                            gateName={gateName}
                            direction={direction}
                            level={level}
                            className={footerNameStyle}
                        />
                    )}
                </div>
                <PTZRequstStatus ptzRequestStatus={ptzRequestStatus} />
                <CameraStreamFooterActions
                    isPTZCamera={isPTZCamera}
                    videoWallCameras={videoWallCameras}
                    togglePTZ={togglePTZ}
                    isPTZOn={isPTZOn}
                    fullZoomIn={fullZoomIn}
                    fullZoomOut={fullZoomOut}
                    ptzRequestStatus={ptzRequestStatus}
                    isCameraFocused={isCameraFocused}
                    audioEnabled={audioEnabled}
                    isCameraActive={isCameraActive}
                    isAudioAvailable={isAudioAvailable}
                    onToggleAudio={onToggleAudio}
                    enableDemoAudioFile={enableDemoAudioFile}
                    camVideoId={camVideoId}
                    camId={camId}
                    camConfig={camConfig}
                    isTransactionPage={isTransactionPage}
                    isDisabledSnapshot={isDisabledSnapshot}
                    onTakeSnapshot={onTakeSnapshot}
                    onFaceMatch={handleFaceMatch}
                    rotation={rotation}
                    expandedCamId={expandedCamId}
                    isMtxDirect={isMtxDirect}
                    refreshVideoStreamHandler={refreshVideoStreamHandler}
                    hardRefreshVideoStreamHandler={hardRefreshVideoStreamHandler}
                    isFullScreenRequested={isFullScreenRequested}
                    openDialogHandler={openDialogHandler}
                    footerMode={footerMode}
                    hasCameraIssues={hasCameraIssues}
                    clickHandlerCameraBugIcon={clickHandlerCameraBugIcon}
                    footerItemsStyle={footerItemsStyle}
                    isCameraFreezed={isCameraFreezed}
                    transactionId={transactionId}
                    visibleCameraIds={visibleCameraIds}
                    openWindowVideoIdOutput={openWindowVideoIdOutput}
                    isStreamTestPage={isStreamTestPage}
                />
            </div>
            {snapshots}
        </>
    );
}

export default memo(CameraStreamFooter);
